import { useState } from 'react';
import { toast } from '@theonefamily/ui';
import { useApi } from '../hooks/useApi.js';
import { post } from '../lib/api.js';

interface PostureEvent {
  id: string;
  event_type: string;
  severity: string;
  user_upn: string;
  user_display_name: string;
  description: string;
  detected_at: string;
  status: string;
}

interface SummaryResponse {
  data: {
    total_events: number;
    new_events: number;
    events_by_severity: Record<string, number>;
    connected_tenants: number;
    last_scan_at: string | null;
  };
}

interface EventsResponse {
  data: PostureEvent[];
  hasMore?: boolean;
  new_count: number;
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  suspicious_signin: 'Suspicious Sign-in',
  oauth_app_grant: 'OAuth App Grant',
  forwarding_rule_added: 'Forwarding Rule',
  admin_role_change: 'Admin Change',
  mfa_failure_spike: 'MFA Failure Spike',
  bulk_download: 'Bulk Download',
  external_sharing_change: 'External Sharing',
  new_admin_added: 'New Admin Added',
  inbox_rule_delete_all: 'Inbox Rule Delete',
  guest_user_added: 'Guest User Added',
};

export function SaasPosturePage() {
  const { data: eventsData, loading, error, refresh } = useApi<EventsResponse>('/protect/saas-posture?action=events');
  const { data: summaryData } = useApi<SummaryResponse>('/protect/saas-posture?action=summary');
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const events = eventsData?.data ?? [];
  const summary = summaryData?.data;

  async function handleUpdateStatus(eventId: string, status: string) {
    setActionLoading(eventId);
    try {
      await post(`/protect/saas-posture/${eventId}?action=update-status`, { status });
      refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setActionLoading(null);
    }
  }

  function severityBadge(severity: string) {
    const colors =
      severity === 'critical' ? 'bg-red-100 text-red-700' :
      severity === 'high' ? 'bg-orange-100 text-orange-700' :
      severity === 'medium' ? 'bg-yellow-100 text-yellow-700' :
      'bg-emerald-100 text-emerald-700';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{severity}</span>;
  }

  function statusBadge(status: string) {
    const colors =
      status === 'new' ? 'bg-red-100 text-red-700' :
      status === 'investigating' ? 'bg-yellow-100 text-yellow-700' :
      status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
      'bg-gray-100 text-gray-600';
    return <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors} capitalize`}>{status.replace('_', ' ')}</span>;
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading SaaS posture data...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">SaaS Security Posture</h2>
      <p className="text-gray-600 mb-6">Monitor M365 security events, OAuth app grants, and admin changes.</p>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Connected Tenants</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.connected_tenants ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">New Events</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{summary?.new_events ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Events</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary?.total_events ?? 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Last Scan</p>
          <p className="text-lg font-bold text-gray-900 mt-1">
            {summary?.last_scan_at ? new Date(summary.last_scan_at).toLocaleString() : 'Never'}
          </p>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Recent Events</h3>
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Detail</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">User</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Severity</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Time</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No posture events detected. Connect an M365 tenant to start monitoring.</td></tr>
              ) : events.map((e) => (
                <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-600">{EVENT_TYPE_LABELS[e.event_type] ?? e.event_type}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{e.description}</td>
                  <td className="px-4 py-3 text-gray-600">{e.user_display_name || e.user_upn}</td>
                  <td className="px-4 py-3">{severityBadge(e.severity)}</td>
                  <td className="px-4 py-3">{statusBadge(e.status)}</td>
                  <td className="px-4 py-3 text-gray-500">{new Date(e.detected_at).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right">
                    {e.status === 'new' && (
                      <button
                        onClick={() => handleUpdateStatus(e.id, 'investigating')}
                        disabled={actionLoading === e.id}
                        className="text-xs text-blue-600 hover:text-blue-800 mr-2 disabled:opacity-50"
                      >
                        Investigate
                      </button>
                    )}
                    {(e.status === 'new' || e.status === 'investigating') && (
                      <button
                        onClick={() => handleUpdateStatus(e.id, 'resolved')}
                        disabled={actionLoading === e.id}
                        className="text-xs text-emerald-600 hover:text-emerald-800 disabled:opacity-50"
                      >
                        Resolve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
